import { useState } from 'react';
import {
  Box, Grid, Stack, Typography, Card, Checkbox, Alert, Chip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Collapse, IconButton,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useSelector } from 'react-redux';
import {
  TAT_FREIGHT_DIRECTIONS, TAT_MODE_ROWS,
  stNameFromGroups, groupLabelFromApiGroup,
  derivedStsForMode, resolveStsFromProfile, filterPricedCodes, findStGroup,
} from '../constants';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';

function SelectableCard({ checked, children }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: checked ? 'primary.main' : 'divider',
        bgcolor: checked ? 'primary.lighter' : 'background.paper',
        cursor: 'default',
      }}
    >
      <Box sx={{ p: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Checkbox checked={checked} disabled sx={{ p: 0.25, pointerEvents: 'none' }} />
          <Box sx={{ flex: 1, color: checked ? 'primary.dark' : 'text.secondary' }}>{children}</Box>
        </Stack>
      </Box>
    </Card>
  );
}

const MONO = 'JetBrains Mono, ui-monospace, monospace';

function AccountCodeChip({ code, payment }) {
  const pmLabel = payment === 'BOTH' ? 'Both' : payment === 'COD' ? 'COD' : 'Prepaid';
  const pmColor = payment === 'BOTH' ? 'default' : payment === 'COD' ? 'warning' : 'info';
  return (
    <Chip
      size="small"
      color={pmColor}
      label={(
        <span>
          {code}
          <Box component="span" sx={{ ml: 0.75, px: 0.6, py: 0.1, borderRadius: 0.75, fontSize: 9, bgcolor: 'rgba(0,0,0,0.08)' }}>
            {pmLabel}
          </Box>
        </span>
      )}
      sx={{ fontFamily: MONO, fontSize: 10.5, height: 22 }}
    />
  );
}

// Resolved service-type card with a VIEW-ONLY parent → account-code drill-down
// (mirrors the Pricing wizard's ServiceTypeCard coverage tree). Selection stays
// driven by Mode × Size above; courier mapping is managed at the CP master.
function ResolvedStCard({ item }) {
  const [open, setOpen] = useState(false);
  const { data: groups = [] } = useGetServiceabilityGroupsQuery();
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const allLinked = (findStGroup(groups, item.code)?.linkedCodes || []).filter((lc) => lc.isActive !== false);
  // Only the couriers/account codes the merchant has pricing for (matches by
  // prozo_id, AC code or parent). Empty pricedKeys = pricing not loaded → show all.
  const linkedCodes = filterPricedCodes(allLinked, pricedKeys);
  const parentKeys = [...new Set(linkedCodes.map((lc) => lc.parent || lc.cpAccountCode))];
  const parents = parentKeys.map((pk) => ({ parent: pk, codes: linkedCodes.filter((lc) => (lc.parent || lc.cpAccountCode) === pk) }));
  const codeCount = linkedCodes.length;

  return (
    <Card variant="outlined" sx={{ borderColor: 'primary.light', bgcolor: 'primary.lighter' }}>
      <Box sx={{ p: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 1, minWidth: 0 }} noWrap title={item.name}>
            {item.name}
          </Typography>
          <Chip
            size="small"
            label={item.code}
            sx={{ fontFamily: MONO, fontSize: 10, height: 18, bgcolor: '#fff', color: 'text.secondary' }}
          />
          <Box sx={{ flex: 1 }} />
          {!item.mapped && (
            <Chip size="small" color="warning" variant="outlined" label="No couriers mapped" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
          )}
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontFamily: MONO, fontSize: 10.5 }}>
          ↳ {item.derivation}
        </Typography>
      </Box>

      {/* Coverage tree — view-only */}
      {parents.length > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'primary.light' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1.25, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.55)' } }}
            onClick={() => setOpen((v) => !v)}
          >
            <IconButton size="small" sx={{ p: 0.25, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
            <Chip label={`${parents.length} parent${parents.length !== 1 ? 's' : ''}`} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
            <Chip label={`${codeCount} account code${codeCount !== 1 ? 's' : ''}`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#ede9fe', color: '#5b21b6' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto !important' }}>
              {open ? 'collapse' : 'view mapping'}
            </Typography>
          </Stack>
          <Collapse in={open}>
            <Box sx={{ p: 1.25, pt: 0.75, bgcolor: 'background.paper' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mb: 1 }}>
                View-only — courier ↔ account-code mapping is managed at the CP master.
              </Typography>
              <Stack spacing={1.25}>
                {parents.map((p) => (
                  <Box key={p.parent}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', display: 'block', mb: 0.5 }}>
                      {p.parent}
                      <Chip label={`${p.codes.length} code${p.codes.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 0.75, height: 16, fontSize: 9.5 }} />
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75}>
                      {p.codes.map((c) => <AccountCodeChip key={c.cpAccountCode} code={c.cpAccountCode} payment={c.payment} />)}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      )}
    </Card>
  );
}

function SectionTitle({ icon, title, badge }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13.5 }}>
        {icon} {title}
      </Typography>
      {badge && <Chip size="small" label={badge} sx={{ height: 18, fontSize: 10, bgcolor: 'grey.100', color: 'text.secondary' }} />}
    </Stack>
  );
}

// Slim Operating Profile — the Pricing wizard's profile stage reduced to the
// essentials: freight direction + mode × size. Service types resolve
// automatically and the rest of the TAT flow runs only on them.
export default function StageProfile() {
  const profile = useSelector((s) => s.tatWizard.profile);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const { data: groups = [] } = useGetServiceabilityGroupsQuery();

  const resolved = resolveStsFromProfile(profile);
  const hasDirection = Object.values(profile.directions).some(Boolean);

  // Group resolved STs by taxonomy group for the read-only summary.
  const grouped = [];
  resolved.forEach(({ code, derivation }) => {
    const apiGroup = findStGroup(groups, code);
    const stName = stNameFromGroups(code, groups);
    const stGroup = groupLabelFromApiGroup(apiGroup);
    let bucket = grouped.find((g) => g.group === stGroup);
    if (!bucket) { bucket = { group: stGroup, items: [] }; grouped.push(bucket); }
    const activeCodes = (apiGroup?.linkedCodes || []).filter((lc) => lc.isActive !== false);
    const pricedCodes = filterPricedCodes(activeCodes, pricedKeys);
    const mapped = pricedCodes.length > 0;
    bucket.items.push({ code, name: stName, group: stGroup, derivation, mapped });
  });

  return (
    <Box>
      <Alert severity="info" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2.5 }}>
        This profile is <b>pre-populated</b> and cannot be edited here. Freight direction and mode × size are set
        during merchant onboarding. The service types in scope are resolved automatically below.
      </Alert>

      {/* A. Freight direction */}
      <SectionTitle icon="🧭" title="Freight Direction" badge="multi-select · required" />
      <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
        {TAT_FREIGHT_DIRECTIONS.map((d) => (
          <Grid item xs={12} md={4} key={d.key}>
            <SelectableCard checked={profile.directions[d.key]}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.label}</Typography>
            </SelectableCard>
          </Grid>
        ))}
      </Grid>

      {/* B. Mode of Delivery × Size */}
      <Box sx={{ mt: 3 }}>
        <SectionTitle icon="🚚" title="Mode of Delivery × Size" badge="multi-select · per row" />
        <TableContainer component={Box} sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1.25, bgcolor: '#fff' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'background.neutral', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' } }}>
                <TableCell>Mode of Delivery</TableCell>
                <TableCell sx={{ width: 140 }}>Regular</TableCell>
                <TableCell sx={{ width: 140 }}>Large</TableCell>
                <TableCell>Resulting service types</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TAT_MODE_ROWS.map((m) => {
                const row = profile.modes[m.k] || { reg: false, large: false };
                const derived = derivedStsForMode(m.k, row.reg, row.large);
                return (
                  <TableRow key={m.k}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.lbl}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox size="small" checked={row.reg} disabled />
                        <Typography variant="caption">Regular</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox size="small" checked={row.large} disabled />
                        <Typography variant="caption">Large</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', color: 'text.secondary' }}>
                        {derived.length ? derived.join('  ·  ') : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* C. Auto-resolved service types (read-only) */}
      <Divider sx={{ my: 3 }} />
      <Stack direction="row" spacing={1} alignItems="center">
        <SectionTitle icon="🧩" title="Service Types in scope" badge="resolved automatically" />
        <Chip
          size="small"
          color={resolved.length ? 'primary' : 'default'}
          label={`${resolved.length} selected`}
          sx={{ height: 20, fontWeight: 700 }}
        />
      </Stack>

      {!hasDirection || resolved.length === 0 ? (
        <Alert severity="warning" sx={{ mt: 1.5 }} icon={<span>⚠️</span>}>
          {!hasDirection
            ? 'Select at least one Freight Direction to resolve service types.'
            : 'No service types resolve from the current profile — tick at least one Mode × Size above.'}
        </Alert>
      ) : (
        grouped.map(({ group, items }) => (
          <Box key={group} sx={{ mt: 2 }}>
            <Typography variant="overline" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
              {group} ({items.length})
            </Typography>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              {items.map((it) => (
                <Grid item xs={12} md={6} key={it.code}>
                  <ResolvedStCard item={it} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}
    </Box>
  );
}
